import _ from 'lodash'
import moment from 'moment'
import React from 'react'
import actions from 'shared_src/actions'
import reminder from 'shared_src/reminder'

import { Can } from '../../ability/context'
import { Permission, Action } from '../../ability/types'
import draft from '../../lib/draft'
import http from '../../lib/http'
import { CheckboxInput, Draft, SaveButton, TextAreaInput, Validators, bindTargetMixin } from '../shared/FormBuilder'
import {
  Button,
  Card,
  Modal,
  RequiredSpan
} from '../shared/NarusMaterial'

import { NewClaimsWarning } from './new-claims-warning'
import {
  formattedSeconds,
  renderDispositionAndReasonStuff,
  renderEncounterTime,
  renderEncounterType,
  renderNarrative,
  renderPhoneEncounterStuff,
  renderUrgency,
  renderReportType,
  renderDoNotBill
} from './shared'
import { UnsavedDrafts } from './unsaved-drafts'

import './styles.scss'

function RenderCheckboxSupervisor ({ supervisor, supervisorDoc, supervisorName, supervisorHasAccess, escalateToSupervisor, disabled }) {
  if (supervisor === supervisorDoc) {
    let label = `Escalate to Supervisor\n${supervisor ? supervisorName ? '(' + supervisorName + ')' : '' : '(Not assigned)'}`
    return (
      <CheckboxInput
        label={label}
        disabled={disabled || !supervisor || !supervisorHasAccess}
        checked={escalateToSupervisor}
        name='escalateToSupervisor'
      />
    )
  } else {
    return (
      <CheckboxInput
        label='Escalate to Supervisor'
        disabled={disabled || !supervisor}
        checked={escalateToSupervisor}
        name='escalateToSupervisor'
      />
    )
  }
}

export const calculateEncounterDuration = (statusHistory, status) => {
  if (!statusHistory) {
    return 0
  }

  const now = moment()

  return statusHistory
    .filter(sh => sh.status === status)
    .map(sh => {
      // if there is no end, assume that it is the current state
      if (!sh.period.end) {
        const start = moment(sh.period.start)
        return Math.floor(now.diff(start) / 1000)
      }
      const end = moment(sh.period.end)
      const start = moment(sh.period.start)
      return Math.floor(end.diff(start) / 1000)
    })
    .reduce((total, number) => total + number)
}

export class SignEncounterForm extends bindTargetMixin(React.Component) {
  constructor (props) {
    super(props)

    if (props.savedFormState && Object.keys(props.savedFormState).length > 0) {
      this.state = {
        value: { ...props.savedFormState },
        ignoreDiscardDialog: true,
        showExcessTimeDialog: false
      }
    } else {
      this.state = {
        value: {
          time: 'computed',
          type: _.get(props, 'encounter.document.type'),
          escalateToSupervisor: _.get(
            props,
            'encounter.document.escalateToSupervisor',
            false
          ),
          escalateToMedicalDirector: _.get(
            props,
            'encounter.document.escalateToMedicalDirector',
            false
          ),
          narrative: _.get(props, 'encounter.document.narrative', ''),
          reportType: _.get(props, 'encounter.document.reportType', null),
          urgency: _.get(props, 'encounter.document.urgency', null),
          agentDoNotBill: _.get(props, 'encounter.document.agentDoNotBill', null),

          participant: null,
          reason: null,
          origin: null,
          medicalDirector: props.medicalDirector,
          supervisor: props.supervisor
        },
        ignoreDiscardDialog: true,
        showExcessTimeDialog: false
      }
    }

    this.loadDraft = this.loadDraft.bind(this)
    this.loadDrafts = this.loadDrafts.bind(this)
    this.isEncounterTimeExcess60Minutes = this.isEncounterTimeExcess60Minutes.bind(this)
  }

  componentDidMount () {
    this.validate(false)
    this.loadDraft()
    this.loadDrafts()
    this.intervalHandler = setInterval(() => this.forceUpdate(), 1000)
  }

  componentDidUpdate (prevProps, prevState) {
    let previousType = prevProps.encounter?.document?.type
    let isTypeChange = previousType !== this.props.encounter?.document?.type
    let previousTypeHadDisposition = [ 'home', 'phone', 'email', 'mobile' ].includes(previousType)

    if (isTypeChange && previousTypeHadDisposition) {
      this.setState(state => ({
        value: {
          ...state.value,
          reason: null,
          participant: null,
          outcome: null
        }
      }), () => {
        this.validate()
      })
    }

    if (isTypeChange && !previousTypeHadDisposition) {
      this.validate()
    }

    if (prevProps.encounter?.subject_id !== this.props.encounter?.subject_id) {
      this.loadDrafts()
    }

    if (prevProps.draft?.resource !== this.props.draft?.resource) {
      this.loadDraft()
    }
  }

  componentWillUnmount () {
    this.props.saveFormState?.(this.state.value)
    clearInterval(this.intervalHandler)
  }

  loadDraft () {
    let id = this.props.draft

    if (!id) {
      return
    }

    this.setState({
      draft: {
        getting: true
      }
    })

    draft
      .get(this.props.draft)
      .then(({ data }) => {
        this.setState({
          draft: {
            get: true,
            data
          }
        })

        if (data) {
          this.setState({ value: data }, () => {
            this.validate()
          })
        }
      })
      .catch(error => {
        this.setState({
          draft: {
            error
          }
        })
      })
  }

  loadDrafts () {
    let id = this.props.encounter?.subject_id

    if (!id) {
      return
    }

    http.getRequest(`/patients/${id}/drafts`)
      .then(({ drafts }) => {
        this.setState({ drafts })
      })
  }

  renderExcessTimeDialog () {
    const { onSign } = this.props
    const buttons = [
      <Button
        style={{ marginLeft: '1em' }}
        label='Yes, Sign'
        onClick={() => {
          onSign(this.state.value)
        }}
        disabled={this.props.isSigning}
        key='a'
      />,
      <Button
        label='No, Discard'
        grey
        onClick={() => this.setState({ showExcessTimeDialog: false })}
        key='b'
      />
    ]

    return (
      this.state.showExcessTimeDialog && (
        <Modal
          title='Are you sure that the following encounter time is accurate?'
          actions={buttons}
          open
        >
          <p>
            Current encounter time:
            {' '}
            <strong>{formattedSeconds(this.checkExcessTime())}</strong>
          </p>
        </Modal>
      )
    )
  }

  isEncounterTimeExcess60Minutes () {
    const { onSign } = this.props
    if (this.checkExcessTime() > 3600) {
      this.setState({ showExcessTimeDialog: true })
    } else {
      this.setState({ showExcessTimeDialog: false })
      onSign(this.state.value)
    }
  }

  checkExcessTime () {
    const duration = calculateEncounterDuration(
      this.props.encounter.statusHistory,
      'in-progress'
    )

    const {
      value: {
        time,
        timeHours,
        timeMinutes
      }
    } = this.state
    if (time === 'override') {
      return (timeHours || 0) * 60 * 60 + (timeMinutes || 0) * 60
    }
    if (time === 'computed') {
      return duration
    }
    return null
  }

  renderWarning () {
    const type = _.get(this.state, 'value.type')
    const status = this.props.caseStatus
    const participant = _.get(this.state, 'value.participant')
    const outcome = _.get(this.state, 'value.outcome')
    const action = actions.sign({
      type,
      status,
      participant,
      outcome
    })
    return (
      action && (
        <div style={{ marginTop: '15px' }}>
          <div style={{ marginBottom: '10px', border: '2px solid orange', padding: '10px', borderRadius: '10px' }}>
            <div style={{ float: 'left' }}>
              <i className='fa fa-exclamation-triangle exclamation-yellow' />
            </div>
            <div style={{ marginLeft: '30px' }}>{action.warning}</div>
          </div>
        </div>
      )
    )
  }

  renderSatisfactionWarning () {
    const status = this.props.caseStatus
    const satisfaction = _.get(this.props, 'settings.satisfaction')
    const encountersHistory = this.props.encountersHistory || []
    const satisfactionSurveyOffered = _.get(
      this.state,
      'value.satisfactionSurveyOffered'
    )
    const last = _.max(
      encountersHistory
        .filter(
          e =>
            e.document.satisfactionSurveyOffered && e.status === 'completed'
        )
        .map(e => moment(e.document.encounterEndTime))
    )
    const warning = reminder.satisfaction.warning({
      status,
      satisfaction,
      satisfactionSurveyOffered,
      last
    })

    return (
      warning && (
        <div style={{ marginTop: '10px' }}>
          <Card
            barColor='orange'
            height='auto'
          >
            <div style={{ float: 'left' }}>
              <i className='fa fa-exclamation-triangle exclamation-yellow' />
            </div>
            <div style={{ marginLeft: '30px' }}>{warning}</div>
          </Card>
        </div>
      )
    )
  }

  render () {
    const {
      value,
      drafts
    } = this.state
    const {
      encounter,
      isSigning,
      supervisor,
      medicalDirector,
      clientOrg,
      clientAttributes,
      profile
      // clientName
    } =
      this.props
    // const encType = _.get(encounter, 'document.type', '')
    const {
      supervisor: supervisorDoc,
      supervisor_name: supervisorName,
      supervisor_orgs: supervisorOrgs,
      medical_director: medicalDirectorDoc,
      medical_director_name: medicalDirectorName,
      medical_director_orgs: medicalDirectorOrgs
    } = _.get(encounter, 'document.user.app_metadata', {})

    const supervisorHasAccess = (supervisorOrgs || [])
      .includes(clientOrg)
    const medicalDirectorHasAccess = (Array.isArray(medicalDirectorOrgs) ? medicalDirectorOrgs : [])
      .includes(
        clientOrg
      )

    const disabled = isSigning || drafts?.length > 0 || typeof drafts === 'undefined'
    const role = _.get(profile, 'app_metadata.roles.meta.role')
    const isAdmin = [ 'administrator', 'executive_director', 'medical_director' ].includes(role)

    let satisfactionSurvey = null
    let dischargePlanning = null
    let aps = null
    let responseFindings = null
    let satisfactionSurveyWarning = null

    let encType = this.state.value.type

    if (encType === 'cms') {
      if (this.state.value.reportType === 'APS') {
        aps = (
          <div>
            <h2>
              Present Medical Status
              {' '}
              <RequiredSpan text='Value' />
            </h2>
            <TextAreaInput
              name='presentMedicalStatus'
              label='Present Medical Status'
              value={this.state.presentMedicalStatus}
              validators={[ Validators.required() ]}
            />
            <h2>
              Current and Potential Care Costs
              {' '}
              <RequiredSpan text='Value' />
            </h2>
            <TextAreaInput
              name='careCosts'
              label='Current and Potential Care Costs'
              value={this.state.careCosts}
              validators={[ Validators.required() ]}
            />
            <h2>
              Recommendations
              {' '}
              <RequiredSpan text='Value' />
            </h2>
            <TextAreaInput
              name='recommendations'
              label='Recommendations'
              value={this.state.recommendations}
              validators={[ Validators.required() ]}
            />
          </div>
        )
      } else if (this.state.value.reportType === 'Research') {
        responseFindings = (
          <div>
            <h2>
              Question / Request
              {' '}
              <RequiredSpan text='Value' />
            </h2>
            <TextAreaInput
              name='questionRequest'
              label='Question / Request'
              value={this.state.questionRequest}
              validators={[ Validators.required() ]}
            />
            <h2>
              Response / Findings
              {' '}
              <RequiredSpan text='Value' />
            </h2>
            <TextAreaInput
              name='responseFindings'
              label='Response / Findings'
              value={this.state.responseFindings}
              validators={[ Validators.required() ]}
            />
            <h2>
              Cost Savings
              {' '}
              <RequiredSpan text='Value' />
            </h2>
            <TextAreaInput
              name='costSavings'
              label='Cost Savings'
              value={this.state.costSavings}
              validators={[ Validators.required() ]}
            />
          </div>
        )
      } else {
        dischargePlanning = (
          <div>
            <h2>
              Discharge Planning
              {' '}
              <RequiredSpan text='Value' />
            </h2>
            <TextAreaInput
              name='dischargeplanning'
              label='Discharge Planning'
              validators={[ Validators.required() ]}
            />
          </div>
        )
      }
      localStorage.setItem('reportType', this.state.value.reportType)
    }

    if (encType === 'phone' || encType === 'email' || encType === 'mobile') {
      satisfactionSurveyWarning = this.renderSatisfactionWarning()
    }

    const duration = calculateEncounterDuration(
      encounter.statusHistory,
      'in-progress'
    )

    return (
      <>

        {this.props.documentEncounterType === 'patient' &&
          renderEncounterType(
            this.props.clientName,
            this.props.profile,
            this.props.documentEncounterType
          )
        }
        {(this.state.value.type === 'phone' || encType === 'email' || encType === 'mobile') && renderPhoneEncounterStuff()}
        {(encType === 'phone' || encType === 'home' || encType === 'email' || encType === 'mobile') && (
          <div className='section'>
            {renderDispositionAndReasonStuff({
              episodeOfCare: {
                status: this.props.caseStatus,
                dead: this.props.isPatientDead
              },
              organization: this.props.userManagingOrg,
              encType,
              origin: value.origin,
              participant: value.participant,
              clientAttributes,
              reason: value.reason
            })}

            {this.renderWarning()}
            {satisfactionSurvey}
            {satisfactionSurveyWarning}
          </div>
        )}

        {this.renderExcessTimeDialog()}
        {renderReportType(encounter)}
        {renderUrgency(encounter)}
        {isAdmin && renderDoNotBill(encounter)}

        <div className='section'>
          {renderEncounterTime(value, duration)}
        </div>

        {dischargePlanning}
        {aps}
        {responseFindings}

        <div className='section'>
          <h2>
            Electronic Signature
            {' '}
            <RequiredSpan text='Value' />
          </h2>
          {renderNarrative(encounter)}
        </div>

        {typeof this.props.encounter?.subject_id === 'string' && typeof this.props.clientName === 'string' && (
          <NewClaimsWarning
            clientName={this.props.clientName}
            patientId={this.props.encounter.subject_id}
          />
        )}

        {typeof this.props.encounter?.subject_id === 'string' && typeof this.props.clientName === 'string' && (
          <UnsavedDrafts
            drafts={drafts}
            clientName={this.props.clientName}
            patientId={this.props.encounter.subject_id}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px', alignItems: 'end' }}>
          <div style={{ display: 'flex', gap: '25px' }}>
            <Can
              I={Action.READ}
              a={Permission.ESCALATE_SUMMARY}
            >
              <div style={{ whiteSpace: 'pre-wrap' }}>
                <RenderCheckboxSupervisor
                  supervisor={supervisor}
                  escalateToSupervisor={this.state.escalateToSupervisor}
                  supervisorDoc={supervisorDoc}
                  supervisorHasAccess={supervisorHasAccess}
                  supervisorName={supervisorName}
                  disabled={disabled}
                />
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                <RenderCheckboxMedical
                  medicalDirector={medicalDirector}
                  escalateToMedicalDirector={this.state.escalateToMedicalDirector}
                  medicalDirectorDoc={medicalDirectorDoc}
                  medicalDirectorName={medicalDirectorName}
                  medicalDirectorHasAccess={medicalDirectorHasAccess}
                  disabled={disabled}
                />
              </div>
            </Can>
          </div>
          <div>
            <Draft />
            <SaveButton
              formKey={this.props.formKey}
              label='Sign'
              disabled={disabled}
              ignoreGlobalDisable
              onClick={this.isEncounterTimeExcess60Minutes}
            />
          </div>
        </div>
        {this.state.value.escalateToSupervisor && (
          <TextAreaInput
            name='escalateToSupervisorComments'
            label='Comments for Supervisor'
            validators={[ Validators.required() ]}
          />
        )}
        {this.state.value.escalateToMedicalDirector && (
          <TextAreaInput
            name='escalateToMedicalDirectorComments'
            label='Comments for Medical Director'
            validators={[ Validators.required() ]}
          />

        )}
      </>
    )
  }
}

function RenderCheckboxMedical ({ medicalDirector, medicalDirectorDoc, medicalDirectorName, disabled, medicalDirectorHasAccess, escalateToMedicalDirector }) {
  if (medicalDirector === medicalDirectorDoc) {
    let label = `Escalate to Medical Director\n${medicalDirector ? medicalDirectorName ? '(' + medicalDirectorName + ')' : '' : '(Not assigned)'}`
    let disabledCheckbox = disabled || !medicalDirector || !medicalDirectorHasAccess
    return (
      <CheckboxInput
        label={label}
        disabled={disabledCheckbox}
        checked={escalateToMedicalDirector}
        name='escalateToMedicalDirector'
      />
    )
  } else {
    return (
      <CheckboxInput
        label='Escalate to Medical Director'
        disabled={disabled || !medicalDirector}
        checked={escalateToMedicalDirector}
        name='escalateToMedicalDirector'
      />
    )
  }
}
